import {
  HelpCircle,
  DollarSign,
  Briefcase,
  UserCheck,
  Shield,
  Globe,
  Clock,
  Award,
  Settings,
  FileText,
} from "lucide-react";

export interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
}

export interface FAQCategory {
  id: string;
  name: string;
  icon: any;
  description: string;
}

export const faqCategories: FAQCategory[] = [
  {
    id: "getting-started",
    name: "Getting Started",
    icon: HelpCircle,
    description: "New to Unimerg? Start here",
  },
  {
    id: "payments",
    name: "Payments & Earnings",
    icon: DollarSign,
    description: "Everything about compensation",
  },
  {
    id: "projects",
    name: "Projects & Tasks",
    icon: Briefcase,
    description: "How work is assigned and completed",
  },
  {
    id: "qualifications",
    name: "Qualifications & Skills",
    icon: UserCheck,
    description: "Requirements and certifications",
  },
  {
    id: "account",
    name: "Account & Profile",
    icon: Settings,
    description: "Managing your account",
  },
  {
    id: "privacy",
    name: "Privacy & Security",
    icon: Shield,
    description: "Data protection and safety",
  },
  {
    id: "global",
    name: "Global & Remote Work",
    icon: Globe,
    description: "Working from anywhere",
  },
  {
    id: "scheduling",
    name: "Scheduling & Availability",
    icon: Clock,
    description: "Flexible work arrangements",
  },
  {
    id: "certifications",
    name: "Certifications & Growth",
    icon: Award,
    description: "Career development",
  },
  {
    id: "legal",
    name: "Legal & Compliance",
    icon: FileText,
    description: "Terms and policies",
  },
];

export const faqsData: FAQ[] = [
  // Getting Started
  {
    id: "gs-1",
    question: "What is Unimerg and how does it work?",
    answer:
      "Unimerg is a global platform that connects skilled professionals with AI companies that need human expertise to train, evaluate, and improve their AI systems. You sign up, complete your profile, get matched with relevant projects, complete tasks, and get paid for your contributions.",
    category: "getting-started",
  },
  {
    id: "gs-2",
    question: "How do I create an account?",
    answer:
      "Click the 'Sign Up' button on our homepage, enter your email address, and follow the verification process. You'll need to provide a gate code if you've been invited, or join our waitlist if you're new. Once verified, complete your profile with your skills, languages, and expertise areas.",
    category: "getting-started",
  },
  {
    id: "gs-3",
    question: "What is a gate code and how do I get one?",
    answer:
      "A gate code is a unique invitation code sent to accepted applicants. We review applications on a rolling basis and send gate codes via email to qualified candidates. You can also receive a gate code through referrals from existing contributors.",
    category: "getting-started",
  },
  {
    id: "gs-4",
    question: "How long does the application process take?",
    answer:
      "Most applications are reviewed within 3-5 business days. If approved, you'll receive your gate code via email. During high-volume periods, review times may extend to 1-2 weeks. You can check your application status in your dashboard.",
    category: "getting-started",
  },
  {
    id: "gs-5",
    question: "Can I apply if I don't have AI experience?",
    answer:
      "Absolutely! We value diverse expertise. Many of our tasks require domain knowledge in fields like healthcare, law, education, creative writing, and more. Your unique professional background and language skills are often more valuable than technical AI knowledge.",
    category: "getting-started",
  },
  {
    id: "gs-6",
    question: "What equipment do I need to get started?",
    answer:
      "You need a reliable computer (desktop or laptop), stable internet connection, and a modern web browser (Chrome, Firefox, Safari, or Edge). Some specialized tasks may require additional equipment like a microphone or webcam, which will be specified in the project requirements.",
    category: "getting-started",
  },
  {
    id: "gs-7",
    question: "Is there a mobile app for Unimerg?",
    answer:
      "Currently, Unimerg is optimized for desktop use as most tasks require detailed work that benefits from a larger screen. However, you can access your dashboard and manage your profile via mobile browser. We're developing a mobile app for task notifications and simple activities.",
    category: "getting-started",
  },

  // Payments & Earnings
  {
    id: "pay-1",
    question: "How much can I earn on Unimerg?",
    answer:
      "Earnings vary based on task complexity, your expertise level, and time invested. Rates typically range from $15-$100+ per hour. Specialized roles like Subject Matter Experts can earn even more. Top contributors regularly earn $3,000-$10,000+ monthly.",
    category: "payments",
  },
  {
    id: "pay-2",
    question: "How and when do I get paid?",
    answer:
      "Payments are processed weekly every Friday for work completed through the previous Sunday. We offer multiple payment methods including PayPal, direct bank transfer, Wise, and Payoneer. Funds typically arrive within 3-5 business days after processing.",
    category: "payments",
  },
  {
    id: "pay-3",
    question: "Is there a minimum payout threshold?",
    answer:
      "Yes, the minimum payout threshold is $25. If your balance is below this amount, it will roll over to the next payment cycle. You can view your current balance and payment history in your dashboard.",
    category: "payments",
  },
  {
    id: "pay-4",
    question: "What payment methods are available?",
    answer:
      "We support PayPal, direct bank transfer (ACH for US, SEPA for EU), Wise (TransferWise), and Payoneer. Available methods may vary by country. You can set your preferred payment method in your account settings.",
    category: "payments",
  },
  {
    id: "pay-5",
    question: "Are there any fees deducted from my earnings?",
    answer:
      "Unimerg does not deduct any platform fees from your earnings. However, your payment provider (PayPal, bank, etc.) may charge transaction or currency conversion fees. We recommend choosing a payment method with low fees for your country.",
    category: "payments",
  },
  {
    id: "pay-6",
    question: "How do I handle taxes on my earnings?",
    answer:
      "As an independent contractor, you're responsible for reporting and paying taxes on your earnings according to your local tax laws. US-based contributors will receive a 1099 form if earnings exceed $600 annually. We recommend consulting a tax professional.",
    category: "payments",
  },
  {
    id: "pay-7",
    question: "Can I see my earnings in real-time?",
    answer:
      "Yes! Your dashboard displays real-time earnings as you complete tasks. You can view detailed breakdowns by project, day, week, or month, and track your payment history and pending payouts.",
    category: "payments",
  },
  {
    id: "pay-8",
    question: "What happens if there's a payment issue?",
    answer:
      "If you experience a payment issue, contact our support team immediately through your dashboard. Common issues like incorrect payment details or failed transfers are typically resolved within 48 hours. We maintain a dedicated payments support team.",
    category: "payments",
  },
  {
    id: "pay-9",
    question: "Do you offer bonuses or incentives?",
    answer:
      "Yes! We offer performance bonuses for high-quality work, speed bonuses for meeting deadlines, referral bonuses for bringing new contributors, and seasonal incentives during high-demand periods. Check the 'Rewards' section in your dashboard.",
    category: "payments",
  },

  // Projects & Tasks
  {
    id: "proj-1",
    question: "What types of projects are available?",
    answer:
      "Projects include AI training data creation, content evaluation, prompt engineering, data annotation, linguistic analysis, subject matter consultation, content moderation, conversation design, image/video annotation, and audio transcription.",
    category: "projects",
  },
  {
    id: "proj-2",
    question: "How are projects assigned to me?",
    answer:
      "Projects are matched based on your profile skills, language abilities, certifications, performance history, and availability. You'll receive notifications for relevant opportunities and can browse available projects in your dashboard.",
    category: "projects",
  },
  {
    id: "proj-3",
    question: "Can I choose which projects to work on?",
    answer:
      "Yes! While some projects are directly assigned based on your qualifications, most are available for you to select. You can browse the project marketplace, read requirements, and choose tasks that interest you and match your schedule.",
    category: "projects",
  },
  {
    id: "proj-4",
    question: "What if I can't complete a task I've started?",
    answer:
      "If you cannot complete a task, you can release it back to the pool within the specified timeframe without penalty. However, frequently abandoning tasks may affect your reliability score. If you're struggling, reach out to project support for help.",
    category: "projects",
  },
  {
    id: "proj-5",
    question: "How long do tasks typically take?",
    answer:
      "Task duration varies widely. Quick tasks like rating responses may take 1-5 minutes, while complex projects like writing detailed evaluations or creating training data sets can take 30 minutes to several hours. Time estimates are provided for each task.",
    category: "projects",
  },
  {
    id: "proj-6",
    question: "Is there a limit to how many tasks I can do?",
    answer:
      "There's no hard limit on the number of tasks you can complete. However, some projects have daily or weekly caps to ensure quality and fair distribution among contributors. High performers often get access to additional capacity.",
    category: "projects",
  },
  {
    id: "proj-7",
    question: "What happens if my work is rejected?",
    answer:
      "If your work doesn't meet quality standards, you'll receive detailed feedback explaining the issues. Minor errors may be sent back for revision. Consistent quality issues may result in removal from specific projects. We focus on coaching, not punishment.",
    category: "projects",
  },
  {
    id: "proj-8",
    question: "How do I report issues with a project?",
    answer:
      "Each project has a support button for reporting issues like unclear instructions, technical problems, or inappropriate content. Our project managers typically respond within 24 hours. Urgent issues have escalation paths for faster resolution.",
    category: "projects",
  },
  {
    id: "proj-9",
    question: "Can I work on projects in multiple languages?",
    answer:
      "Absolutely! If you're fluent in multiple languages, you can add them to your profile and qualify for multilingual projects. Bilingual and multilingual contributors are highly valued and often have access to premium opportunities.",
    category: "projects",
  },

  // Qualifications & Skills
  {
    id: "qual-1",
    question: "What qualifications do I need to join Unimerg?",
    answer:
      "Basic requirements include being 18+ years old, having reliable internet access, and proficiency in at least one language. Beyond that, we value diverse backgrounds—whether you're a student, professional, retiree, or career changer, your unique skills are welcome.",
    category: "qualifications",
  },
  {
    id: "qual-2",
    question: "Do I need a college degree?",
    answer:
      "No degree is required for most projects. We evaluate candidates based on demonstrated skills, domain expertise, and assessment performance. Many successful contributors have non-traditional educational backgrounds.",
    category: "qualifications",
  },
  {
    id: "qual-3",
    question: "What skills are most in demand?",
    answer:
      "Currently high-demand skills include: native fluency in non-English languages, STEM expertise (especially medicine, law, coding), creative writing ability, attention to detail, critical thinking, and domain expertise in specialized fields.",
    category: "qualifications",
  },
  {
    id: "qual-4",
    question: "How do I prove my expertise?",
    answer:
      "You can demonstrate expertise through qualification assessments, uploading relevant certifications or credentials, completing sample tasks, and building a track record through your work history on the platform.",
    category: "qualifications",
  },
  {
    id: "qual-5",
    question: "Can I add new skills to my profile later?",
    answer:
      "Yes! You can update your skills anytime. Adding new skills may require passing relevant assessments. We encourage continuous skill development and regularly add new certification opportunities.",
    category: "qualifications",
  },
  {
    id: "qual-6",
    question: "What are qualification assessments like?",
    answer:
      "Assessments vary by project type but typically include sample tasks that simulate real work. They test your understanding of guidelines, attention to detail, and domain knowledge. Most assessments take 15-45 minutes and can be retaken after a waiting period.",
    category: "qualifications",
  },

  // Account & Profile
  {
    id: "acc-1",
    question: "How do I update my profile information?",
    answer:
      "Log into your dashboard and navigate to 'Profile Settings.' You can update your personal information, skills, languages, availability, payment details, and notification preferences. Some changes may require re-verification.",
    category: "account",
  },
  {
    id: "acc-2",
    question: "Can I change my email address?",
    answer:
      "Yes, you can change your email in account settings. You'll need to verify the new email address before the change takes effect. Your old email will receive a notification about the change for security purposes.",
    category: "account",
  },
  {
    id: "acc-3",
    question: "How do I reset my password?",
    answer:
      "Click 'Forgot Password' on the login page, enter your email, and follow the reset link sent to your inbox. For security, reset links expire after 24 hours. If you don't receive the email, check your spam folder or contact support.",
    category: "account",
  },
  {
    id: "acc-4",
    question: "Can I have multiple accounts?",
    answer:
      "No, each person is limited to one account. Multiple accounts violate our terms of service and may result in permanent suspension. If you have a legitimate need (like separate business accounts), contact support to discuss options.",
    category: "account",
  },
  {
    id: "acc-5",
    question: "How do I delete my account?",
    answer:
      "You can request account deletion in your account settings. Please note that this is permanent and cannot be undone. Any pending earnings will be paid out, and you'll need to wait 6 months before creating a new account.",
    category: "account",
  },
  {
    id: "acc-6",
    question: "What should I do if my account is suspended?",
    answer:
      "If your account is suspended, you'll receive an email explaining the reason. Common causes include quality issues, policy violations, or suspicious activity. You can appeal through the link in your suspension notice or contact support.",
    category: "account",
  },
  {
    id: "acc-7",
    question: "How do I enable two-factor authentication?",
    answer:
      "Go to 'Security Settings' in your dashboard and click 'Enable 2FA.' You can use an authenticator app (recommended) or SMS verification. We strongly recommend enabling 2FA to protect your account and earnings.",
    category: "account",
  },

  // Privacy & Security
  {
    id: "priv-1",
    question: "How does Unimerg protect my personal data?",
    answer:
      "We use industry-standard encryption, secure servers, and strict access controls. Your personal data is never sold to third parties. We're compliant with GDPR, CCPA, and other privacy regulations. See our Privacy Policy for full details.",
    category: "privacy",
  },
  {
    id: "priv-2",
    question: "What data do you collect about me?",
    answer:
      "We collect information you provide (name, email, skills), work activity data (tasks completed, time spent), and technical data (IP address, device info) for security. We only collect what's necessary to provide our services.",
    category: "privacy",
  },
  {
    id: "priv-3",
    question: "Can I download my data?",
    answer:
      "Yes, you can request a copy of your personal data through the 'Privacy' section in your account settings. We'll compile and send your data within 30 days as required by privacy regulations.",
    category: "privacy",
  },
  {
    id: "priv-4",
    question: "Is my work monitored?",
    answer:
      "We track task completion, quality metrics, and time spent for quality assurance and fair compensation. We do not monitor your personal computer, install tracking software, or access anything outside the Unimerg platform.",
    category: "privacy",
  },
  {
    id: "priv-5",
    question: "Are the projects I work on confidential?",
    answer:
      "Yes, all projects are covered by our confidentiality agreement. You should not share project details, client names, or work samples publicly. Violating confidentiality can result in account termination and legal action.",
    category: "privacy",
  },
  {
    id: "priv-6",
    question: "How do I report a security concern?",
    answer:
      "Report security concerns immediately to security@unimerg.com or through the 'Report Security Issue' option in your dashboard. We take all reports seriously and investigate promptly. We offer bug bounties for responsible disclosure.",
    category: "privacy",
  },

  // Global & Remote Work
  {
    id: "global-1",
    question: "Can I work from any country?",
    answer:
      "Unimerg is available in 10+ countries. However, due to regulatory requirements, we cannot currently accept contributors from certain regions. Check our 'Supported Countries' page for the current list.",
    category: "global",
  },
  {
    id: "global-2",
    question: "Do I need to speak English?",
    answer:
      "While our platform interface is in English, many projects specifically require non-English speakers. We have active projects in 50+ languages, and native speakers of less common languages are especially valuable.",
    category: "global",
  },
  {
    id: "global-3",
    question: "What if I travel or move to a different country?",
    answer:
      "You can update your location in your profile settings. Some projects may have geographic restrictions. Ensure your payment method works in your new location. Temporary travel usually doesn't affect your account.",
    category: "global",
  },
  {
    id: "global-4",
    question: "Are there country-specific opportunities?",
    answer:
      "Yes! Some projects require contributors from specific regions for cultural context, local knowledge, or regulatory compliance. Your location can open unique opportunities not available to contributors elsewhere.",
    category: "global",
  },
  {
    id: "global-5",
    question: "How do time zones affect my work?",
    answer:
      "Most tasks have no time zone restrictions—work whenever suits you. Some real-time projects (like live moderation) may require specific hours. Your availability settings help us match you with suitable projects.",
    category: "global",
  },

  // Scheduling & Availability
  {
    id: "sched-1",
    question: "How flexible are the working hours?",
    answer:
      "Extremely flexible! Most tasks can be done anytime, anywhere. You set your own schedule—work early mornings, late nights, or weekends. There's no minimum hours requirement, though consistent availability helps with project matching.",
    category: "scheduling",
  },
  {
    id: "sched-2",
    question: "Is there a minimum time commitment?",
    answer:
      "There's no strict minimum. However, we recommend at least 5-10 hours weekly to maintain active status and keep your skills sharp. Some premium projects may require minimum availability commitments.",
    category: "scheduling",
  },
  {
    id: "sched-3",
    question: "Can I take breaks or vacations?",
    answer:
      "Absolutely! You can set your status to 'Away' anytime without penalty. For extended breaks (30+ days), let us know so we can pause project assignments. Your account and earnings history are preserved indefinitely.",
    category: "scheduling",
  },
  {
    id: "sched-4",
    question: "How do I set my availability?",
    answer:
      "In your profile, you can specify your typical working hours, preferred days, and maximum weekly hours. This helps us match you with projects that fit your schedule. Update anytime as your availability changes.",
    category: "scheduling",
  },
  {
    id: "sched-5",
    question: "What if I have a full-time job?",
    answer:
      "Many contributors work on Unimerg alongside other employment. The flexible nature of our tasks makes it ideal for supplemental income. Just ensure your primary employer's policies allow outside work and there are no conflicts of interest.",
    category: "scheduling",
  },
  {
    id: "sched-6",
    question: "Are there deadlines for tasks?",
    answer:
      "Most tasks have completion windows ranging from a few hours to several days. Deadlines are clearly displayed when you accept a task. Completing tasks within deadlines positively affects your reliability score.",
    category: "scheduling",
  },

  // Certifications & Growth
  {
    id: "cert-1",
    question: "What certifications does Unimerg offer?",
    answer:
      "We offer certifications in AI training methodologies, data annotation, content evaluation, prompt engineering, and specialized domains like medical, legal, and technical writing. Certifications unlock premium projects and higher pay rates.",
    category: "certifications",
  },
  {
    id: "cert-2",
    question: "Are the certifications free?",
    answer:
      "Yes! All Unimerg certifications are free. We invest in your development because better-trained contributors produce higher quality work. Some advanced certifications require completing prerequisite courses.",
    category: "certifications",
  },
  {
    id: "cert-3",
    question: "Do certifications expire?",
    answer:
      "Most certifications are valid for 12 months. As AI technology evolves, we update our standards and ask contributors to recertify to maintain quality. Recertification is usually faster than initial certification.",
    category: "certifications",
  },
  {
    id: "cert-4",
    question: "How can I advance my career on Unimerg?",
    answer:
      "Progress by maintaining high quality scores, earning certifications, completing diverse projects, and building expertise in specialized areas. Top performers can become Team Leads, Quality Reviewers, or even join our full-time staff.",
    category: "certifications",
  },
  {
    id: "cert-5",
    question: "Is there a contributor ranking system?",
    answer:
      "Yes, contributors are ranked based on quality scores, volume, reliability, and certifications. Higher ranks unlock premium projects, priority support, and special opportunities. Your rank is visible in your dashboard.",
    category: "certifications",
  },
  {
    id: "cert-6",
    question: "Can I use Unimerg experience on my resume?",
    answer:
      "Absolutely! Many contributors list their Unimerg experience on resumes and LinkedIn. We can provide verification of your work history and certifications upon request. Our experience is recognized by many AI companies.",
    category: "certifications",
  },

  // Legal & Compliance
  {
    id: "legal-1",
    question: "What is my employment status?",
    answer:
      "Contributors are independent contractors, not employees. This means you're responsible for your own taxes, insurance, and equipment. You have the freedom to work for other platforms and set your own schedule.",
    category: "legal",
  },
  {
    id: "legal-2",
    question: "Where can I find the Terms of Service?",
    answer:
      "Our Terms of Service are available at the bottom of every page and in your account settings. We recommend reading them carefully. Key points include confidentiality requirements, quality standards, and payment terms.",
    category: "legal",
  },
  {
    id: "legal-3",
    question: "What happens if I violate the Terms of Service?",
    answer:
      "Violations may result in warnings, project removal, or account suspension depending on severity. Serious violations like fraud, harassment, or confidentiality breaches result in immediate termination and may involve legal action.",
    category: "legal",
  },
  {
    id: "legal-4",
    question: "Who owns the work I create?",
    answer:
      "Work created on the platform is owned by Unimerg or our clients as specified in your agreement. You retain no intellectual property rights to submitted work. This is standard for AI training data and enables the work to be used effectively.",
    category: "legal",
  },
  {
    id: "legal-5",
    question: "How do I report misconduct or policy violations?",
    answer:
      "Report issues through our confidential ethics hotline, the 'Report' button on any project, or email ethics@unimerg.com. All reports are investigated, and you're protected against retaliation for good-faith reports.",
    category: "legal",
  },
  {
    id: "legal-6",
    question: "Is there an arbitration agreement?",
    answer:
      "Yes, our Terms of Service include an arbitration clause for dispute resolution. This means disputes are resolved through arbitration rather than court litigation. You may opt out within 30 days of joining.",
    category: "legal",
  },
  {
    id: "legal-7",
    question: "What age do I need to be to join?",
    answer:
      "You must be at least 18 years old to join Unimerg. In some jurisdictions where the age of majority is higher, you must meet that local requirement. Age verification may be required during onboarding.",
    category: "legal",
  },
];
